import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Navbar } from "../components/Navbar";
import PlaceInput from "../components/PlaceInput";
import HapagSpotPanel from "../components/HapagSpotPanel";
import HapagTrackPanel from "../components/HapagTrackPanel";
import OceanRateCompare from "../components/OceanRateCompare";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Container, Ship, Loader2, RefreshCw, CheckCircle2, AlertTriangle,
  Search, MapPin, Anchor,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/cma-cgm`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

function StatusBanner({ status, loading, onRefresh }) {
  const ok = status?.token_ok && (status?.tracking_enabled || status?.pricing_enabled);
  const pending = status?.token_ok && !ok;
  const tone = ok ? "border-emerald-300 bg-emerald-50" : pending ? "border-amber-300 bg-amber-50" : "border-red-300 bg-red-50";
  const Icon = ok ? CheckCircle2 : AlertTriangle;
  const iconColor = ok ? "text-emerald-600" : pending ? "text-amber-600" : "text-red-600";
  return (
    <div className={`rounded-xl border p-4 flex items-start gap-3 ${tone}`} data-testid="cma-status-banner">
      <Icon className={`h-5 w-5 mt-0.5 ${iconColor}`} />
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm flex items-center gap-2">
          CMA CGM Connection
          {status?.token_ok && <Badge variant="outline" className="text-xs">Auth OK</Badge>}
          {status?.tracking_enabled && <Badge className="bg-emerald-600 text-xs">Tracking</Badge>}
          {status?.pricing_enabled && <Badge className="bg-emerald-600 text-xs">Spot Rates</Badge>}
          {status?.location_enabled && <Badge className="bg-emerald-600 text-xs">Locations</Badge>}
        </div>
        <p className="text-xs text-slate-600 mt-1">{status?.note || "Checking…"}</p>
        {status?.granted_scopes?.length > 0 && (
          <p className="text-xs text-slate-500 mt-1">Scopes: {status.granted_scopes.join(", ")}</p>
        )}
        {status?.client_id && <p className="text-[11px] text-slate-400 mt-0.5">Client: {status.client_id} · {status.base_url}</p>}
      </div>
      <Button variant="outline" size="sm" onClick={onRefresh} disabled={loading} data-testid="cma-status-refresh">
        {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
        <span className="ml-1">Re-test</span>
      </Button>
    </div>
  );
}

function TrackTab() {
  const [reference, setReference] = useState("");
  const [type, setType] = useState("auto");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const doTrack = async (force = false) => {
    if (!reference.trim()) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/track`, {
        headers: authHeaders(),
        params: { reference: reference.trim(), type, force },
      });
      setResult(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Tracking failed");
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const s = result?.summary;
  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-2 items-end">
        <div className="flex-1 w-full">
          <Label className="text-xs">Reference (container / BL / booking)</Label>
          <Input
            placeholder="e.g. DFSU1678655"
            value={reference}
            onChange={(e) => setReference(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === "Enter" && doTrack(false)}
            data-testid="cma-track-input"
          />
        </div>
        <select
          className="h-10 rounded-md border border-input bg-background px-3 text-sm"
          value={type}
          onChange={(e) => setType(e.target.value)}
          data-testid="cma-track-type"
        >
          <option value="auto">Auto-detect</option>
          <option value="container">Container</option>
          <option value="bl">BL / Doc</option>
          <option value="booking">Booking</option>
        </select>
        <Button onClick={() => doTrack(false)} disabled={loading} data-testid="cma-track-btn">
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Search className="h-4 w-4 mr-1" />}
          Track
        </Button>
      </div>
      <p className="text-xs text-slate-500">
        Cost control: 1 live CMA CGM call per container per 24h. Repeat lookups within the day are free (cached).
      </p>

      {result && (
        <Card data-testid="cma-track-result">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Container className="h-4 w-4" /> {result.reference}
              {result.cached ? (
                <Badge variant="outline" className="text-xs">cached</Badge>
              ) : (
                <Badge className="bg-blue-600 text-xs">live</Badge>
              )}
              <Button variant="ghost" size="sm" className="ml-auto text-xs" onClick={() => doTrack(true)} data-testid="cma-track-force">
                <RefreshCw className="h-3 w-3 mr-1" /> Force refresh
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div><div className="text-xs text-slate-500">Latest event</div><div className="font-medium">{s?.latest_event || "—"}</div></div>
              <div><div className="text-xs text-slate-500">When</div><div className="font-medium">{s?.latest_datetime || "—"}</div></div>
              <div><div className="text-xs text-slate-500">Location</div><div className="font-medium">{s?.latest_location || "—"}</div></div>
              <div><div className="text-xs text-slate-500">Vessel</div><div className="font-medium">{s?.vessel || "—"}</div></div>
            </div>
            <div className="text-xs text-slate-400">{s?.event_count || 0} events · fetched {result.fetched_at}</div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function PlaceSearchInput({ label, valueCode, onSelect, placeholder, kind = "sea" }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <PlaceInput
        value={valueCode || ""}
        kind={kind}
        allowFreeText={false}
        placeholder={placeholder}
        data-testid={`cma-place-input-${label.replace(/[^a-zA-Z]/g, "").toLowerCase()}`}
        onSelect={(p) => p.code && onSelect({ unlocode: p.code, point_code: p.code, name: p.name })}
      />
      {valueCode && <div className="text-[11px] text-emerald-700 mt-0.5" data-testid="cma-place-selected">Selected: {valueCode}</div>}
    </div>
  );
}

function SpotTab() {
  const [form, setForm] = useState({
    portOfLoading: "", portOfDischarge: "", placeOfOrigin: "", placeOfDelivery: "",
    doorMode: false, commodityCode: "FAK",
    departureDate: "", equipmentGroupIsoCode: "40HC", numberOfContainers: 1, weightPerContainer: 18000,
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const search = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/spot/search`, {
        portOfLoading: form.portOfLoading || null,
        portOfDischarge: form.portOfDischarge || null,
        placeOfOrigin: form.doorMode ? (form.placeOfOrigin || null) : null,
        placeOfDelivery: form.doorMode ? (form.placeOfDelivery || null) : null,
        commodityCode: form.commodityCode || "FAK",
        departureDate: form.departureDate || null,
        requestedEquipments: [{
          equipmentGroupIsoCode: form.equipmentGroupIsoCode,
          numberOfContainers: Number(form.numberOfContainers),
          weightPerContainer: Number(form.weightPerContainer),
        }],
      }, { headers: authHeaders() });
      setResult(r.data);
      toast.success("SpotOn rates retrieved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Spot search failed");
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div><PlaceSearchInput label="POL (port)" valueCode={form.portOfLoading} onSelect={(r) => set("portOfLoading", r.unlocode || r.point_code || "")} placeholder="Type port, e.g. Haiphong, VN" /></div>
        <div><PlaceSearchInput label="POD (port)" valueCode={form.portOfDischarge} onSelect={(r) => set("portOfDischarge", r.unlocode || r.point_code || "")} placeholder="Type port, e.g. Koper, SI" /></div>
        <div><Label className="text-xs">Commodity</Label><Input value={form.commodityCode} onChange={(e) => set("commodityCode", e.target.value)} placeholder="FAK or HS code" data-testid="cma-spot-commodity" /></div>
        <div><Label className="text-xs">Departure date</Label><Input type="date" value={form.departureDate} onChange={(e) => set("departureDate", e.target.value)} data-testid="cma-spot-date" /></div>
        <div>
          <Label className="text-xs">Equipment</Label>
          <select className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={form.equipmentGroupIsoCode} onChange={(e) => set("equipmentGroupIsoCode", e.target.value)} data-testid="cma-spot-equipment">
            <option value="20GP">20' Standard (20GP)</option>
            <option value="40GP">40' Standard (40GP)</option>
            <option value="40HC">40' High Cube (40HC)</option>
            <option value="45HC">45' High Cube (45HC)</option>
            <option value="20RE">20' Reefer (20RE)</option>
            <option value="40RH">40' Reefer (40RH)</option>
          </select>
        </div>
        <div><Label className="text-xs">Qty</Label><Input type="number" min="1" value={form.numberOfContainers} onChange={(e) => set("numberOfContainers", e.target.value)} data-testid="cma-spot-qty" /></div>
        <div><Label className="text-xs">Weight/ctr (kg)</Label><Input type="number" value={form.weightPerContainer} onChange={(e) => set("weightPerContainer", e.target.value)} data-testid="cma-spot-weight" /></div>
      </div>

      <div className="rounded-lg border border-slate-200 p-3">
        <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
          <input type="checkbox" checked={form.doorMode} onChange={(e) => set("doorMode", e.target.checked)} data-testid="cma-door-toggle" />
          Door-to-door (EXW / DAP) — add inland pre/on-carriage
        </label>
        {form.doorMode && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
            <div><PlaceSearchInput label="Place of Origin (pre-carriage)" valueCode={form.placeOfOrigin} kind="all" onSelect={(r) => set("placeOfOrigin", r.unlocode || r.point_code || "")} placeholder="Inland/city, e.g. Hanoi, VN" /></div>
            <div><PlaceSearchInput label="Place of Delivery (on-carriage)" valueCode={form.placeOfDelivery} kind="all" onSelect={(r) => set("placeOfDelivery", r.unlocode || r.point_code || "")} placeholder="Inland/city, e.g. Ljubljana, SI" /></div>
          </div>
        )}
      </div>

      <Button onClick={search} disabled={loading || !form.portOfLoading || !form.portOfDischarge} data-testid="cma-spot-search-btn">
        {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Anchor className="h-4 w-4 mr-1" />}
        Get SpotOn rates
      </Button>

      {result && (
        <div className="space-y-3" data-testid="cma-spot-result">
          <div className="flex items-center gap-2 text-sm flex-wrap">
            <Badge className="bg-[#0b3d6b]">{result.pol} → {result.pod}</Badge>
            {(result.pol_point_code || result.pod_point_code) && (
              <span className="text-xs text-slate-500" data-testid="cma-spot-point-codes">
                point codes: {result.pol_point_code || "—"} → {result.pod_point_code || "—"}
              </span>
            )}
            <Badge variant="outline" className="text-xs">{result.offer_count || 0} offer(s)</Badge>
          </div>

          {result.offer_count === 0 && (
            <p className="text-sm text-amber-700">No SpotOn offers for this lane/date. Try a different departure date or equipment.</p>
          )}

          {result.offers?.map((o, i) => (
            <Card key={o.offer_id || i} className={i === 0 ? "border-emerald-300" : ""} data-testid={`cma-spot-offer-${i}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    {i === 0 && <Badge className="bg-emerald-600 text-xs mb-1">Best rate</Badge>}
                    <div className="text-2xl font-bold text-slate-900">
                      {o.total_amount?.toLocaleString()} <span className="text-base font-semibold text-slate-500">{o.currency}</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      {[o.vessel, o.service, o.transit_days ? `${o.transit_days}d transit` : null].filter(Boolean).join(" · ") || "Ocean freight"}
                    </div>
                  </div>
                  <div className="text-right text-xs text-slate-500 space-y-1">
                    {o.etd && <div>ETD {String(o.etd).slice(0, 10)}</div>}
                    {o.eta && <div>ETA {String(o.eta).slice(0, 10)}</div>}
                    {o.lock_my_price_available && (
                      <Badge className="bg-amber-500 text-xs">Lock {o.lock_duration_hours || 72}h</Badge>
                    )}
                  </div>
                </div>
                {o.included_charges?.length > 0 && (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-xs font-medium text-emerald-700">✓ Included in freight ({o.included_charges.length})</summary>
                    <ul className="mt-1 text-xs text-slate-500 list-disc pl-5">
                      {o.included_charges.map((c, ci) => <li key={ci}>{c.label || c.code}</li>)}
                    </ul>
                  </details>
                )}
                {o.surcharges?.length > 0 && (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-xs font-medium text-slate-600">{o.surcharges.length} surcharge(s)</summary>
                    <table className="mt-1 w-full text-xs">
                      <tbody>
                        {o.surcharges.map((s, si) => (
                          <tr key={si} className="border-t border-slate-100">
                            <td className="py-1 pr-2 text-slate-600">{s.code}</td>
                            <td className="py-1 pr-2 text-slate-500">{s.label}</td>
                            <td className="py-1 text-right font-medium">{s.amount.toLocaleString()} {s.currency}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </details>
                )}
              </CardContent>
            </Card>
          ))}

          <details>
            <summary className="cursor-pointer text-xs text-slate-400">Raw response</summary>
            <pre className="text-xs bg-slate-50 rounded-lg p-3 overflow-auto max-h-96 mt-1">{JSON.stringify(result.raw, null, 2)}</pre>
          </details>
        </div>
      )}
    </div>
  );
}

export default function CmaCgmPage() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadStatus = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/status`, { headers: authHeaders() });
      setStatus(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Status check failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadStatus(); }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <Navbar />
      <div className="h-14"></div>
      <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="cma-cgm-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-[#0b3d6b] flex items-center justify-center">
            <Ship className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight" data-testid="searates-title">SeaRates</h1>
            <p className="text-sm text-slate-500">Live container tracking &amp; spot rates — CMA CGM &amp; Hapag-Lloyd</p>
          </div>
        </div>
        <a href="/rate-finder" data-testid="legacy-scraper-link"
           className="text-xs text-slate-500 hover:text-[#0b3d6b] underline underline-offset-2">
          Legacy rate scraper →
        </a>
      </div>

      <StatusBanner status={status} loading={loading} onRefresh={loadStatus} />

      <Tabs defaultValue="compare" className="w-full">
        <TabsList data-testid="cma-tabs">
          <TabsTrigger value="compare" data-testid="cma-tab-compare"><MapPin className="h-4 w-4 mr-1" /> Compare Rates</TabsTrigger>
          <TabsTrigger value="track" data-testid="cma-tab-track"><Container className="h-4 w-4 mr-1" /> Track & Trace</TabsTrigger>
          <TabsTrigger value="spot" data-testid="cma-tab-spot"><MapPin className="h-4 w-4 mr-1" /> CMA Spot Rates</TabsTrigger>
          <TabsTrigger value="hapag" data-testid="cma-tab-hapag"><Ship className="h-4 w-4 mr-1" /> Hapag Spot</TabsTrigger>
          <TabsTrigger value="hapag-track" data-testid="cma-tab-hapag-track"><Container className="h-4 w-4 mr-1" /> Hapag Track</TabsTrigger>
        </TabsList>
        <TabsContent value="compare" className="mt-4"><OceanRateCompare /></TabsContent>
        <TabsContent value="track" className="mt-4"><TrackTab /></TabsContent>
        <TabsContent value="spot" className="mt-4"><SpotTab /></TabsContent>
        <TabsContent value="hapag" className="mt-4"><HapagSpotPanel /></TabsContent>
        <TabsContent value="hapag-track" className="mt-4"><HapagTrackPanel /></TabsContent>
      </Tabs>
      </div>
    </div>
  );
}
